import { getRelationshipLabel } from '@fs/zion-sls'
import { i18n } from '@fs/zion-locale'
import { isSubstring } from '@fs/zion-ui-select'
import { NO_RELATION_TYPE } from '../../shared/relationships/relationshipConstants'

/**
 * @typedef {Object} RelationshipOption
 * @property {string} key - Composite key, e.g. 'PARENT_CHILD_FIRST'.
 * @property {string} primaryText - Localized label.
 */

/**
 * The pinned first row of the picker, and the default selection on every row.
 *
 * Covers both "the record shows they are not related" and "the record does not say" — the two were
 * split during design, but SLS has no way to store the difference, so they share one key and one
 * label. Selecting it stores nothing and clears any existing relationship.
 *
 * @returns {RelationshipOption} The pinned option.
 */
export const getNoRelationOption = () => ({
  key: NO_RELATION_TYPE,
  primaryText: i18n.t('relationships-card.no-relation-or-unknown', 'No relation or unknown'),
})

/**
 * Build the picker list for one group member: "No relation or unknown" pinned, then everything
 * else alphabetised with no group headings.
 *
 * Labels are gendered by `otherPerson` to match the seeded value, which comes from
 * `getPersonRelationships(person, images, false)` and already resolves Father over Parent
 * (FSS-14997). An ungendered list would omit the value the row opens on. No recorded sex falls
 * back to the generic labels.
 *
 * @param {Object} types - SLS types object.
 * @param {Object} [otherPerson] - The member this row is about; their SEX_CODE genders the labels.
 * @returns {Array<RelationshipOption>} Pinned row, then the alphabetised terms.
 */
export const getRelationshipOptions = (types, otherPerson) => {
  const terms = (types?.relationships ?? []).reduce((acc, rel) => {
    const firstLabel = getRelationshipLabel({ otherPerson, order: 'FIRST', typeData: rel })
    const secondLabel = getRelationshipLabel({ otherPerson, order: 'SECOND', typeData: rel })

    acc.push({ key: `${rel.relType}_FIRST`, primaryText: firstLabel })
    if (firstLabel !== secondLabel) {
      acc.push({ key: `${rel.relType}_SECOND`, primaryText: secondLabel })
    }
    return acc
  }, [])

  terms.sort((a, b) => a.primaryText.localeCompare(b.primaryText))

  return [getNoRelationOption(), ...terms]
}

/**
 * Build the `getOptions` callback for one member's relationship FilterSelect.
 *
 * FilterSelect does not match for you. Matching is substring rather than prefix, so "child"
 * returns Adopted child, Foster child, Godchild, Stepchild and the rest.
 *
 * @param {Object} types - SLS types object.
 * @param {Object} [otherPerson] - The member this row is about; their SEX_CODE genders the labels.
 * @returns {(inputValue: string) => Array<RelationshipOption>} The picker's `getOptions`.
 */
export const createRelationshipOptionGetter = (types, otherPerson) => {
  const options = getRelationshipOptions(types, otherPerson)

  return (inputValue) => {
    if (!inputValue) return options
    return options.filter((option) => isSubstring(inputValue, option.primaryText))
  }
}
